import { ref } from 'vue'

import {
  createVariantTreeBOMValidation,
  getVariantTreeBOMValidationDetails
} from '@/api'

const mapAmbiguousElementsToDetails = (ambiguousElements = []) => ambiguousElements.map(
  ({ ambiguous1Id2 = '', ambiguous2Id2 = '' } = {}) => `${ ambiguous1Id2 } - ${ ambiguous2Id2 }`
)

const wait = ms => new Promise(resolve => setTimeout(resolve, ms))

export function useAmbiguousDetails({ tableHeader }) {
  const ambiguousRowsState = ref({})
  const isRowDetailsBeingChecked = ref(false)
  const skipWaiting = ref(false)

  const getAmbiguousRowState = rowId => ambiguousRowsState.value[rowId] || {
    isChecking: false,
    isExpanded: false,
    details: [],
    bomValidationCorrelationId: null,
    hasFetchedDetails: false,
    hasFailedDetails: false
  }

  const setAmbiguousRowState = (rowId, newState = {}) => {
    ambiguousRowsState.value = {
      ...ambiguousRowsState.value,
      [rowId]: {
        ...getAmbiguousRowState(rowId),
        ...newState
      }
    }
  }

  const getHeaderColumnFromCell = cell => {
    const field = cell?.getField?.()

    if (!field) return null

    const normalizedField = field.replace(/-/g, '.')

    return tableHeader.value.find(
      ({ field: headerField } = {}) => headerField === normalizedField
    ) || null
  }

  const getComponentIdFromCell = cell => {
    const headerColumn = getHeaderColumnFromCell(cell)

    return headerColumn?.type === 'component'
      ? headerColumn.codeId
      : null
  }

  const pollBomValidationStatus = async ({
    combinationId,
    correlationId,
    maxAttempts = 20,
    delay = 2000
  }) => {
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      // eslint-disable-next-line no-await-in-loop
      const response = await getVariantTreeBOMValidationDetails({ combinationId, correlationId })

      if (response.status === 'SUCCESS') {
        return response
      }

      if (response.status === 'FAILED') {
        throw new Error('BOM validation failed')
      }

      // eslint-disable-next-line no-await-in-loop
      await wait(delay)
    }

    throw new Error('BOM validation polling timed out')
  }

  const createAndPollBomValidation = async ({
    combinationId,
    componentId,
    precondition
  }) => {
    const createResponse = await createVariantTreeBOMValidation({
      combinationId,
      componentId,
      precondition
    })

    const { correlationId } = createResponse

    const pollingResponse = await pollBomValidationStatus({
      combinationId,
      correlationId
    })

    return {
      details: mapAmbiguousElementsToDetails(pollingResponse.ambiguousElements),
      correlationId
    }
  }

  const mapBomValidationResponseToDetails = (response, correlationId) => ({
    details: mapAmbiguousElementsToDetails(response.ambiguousElements),
    correlationId
  })

  const getAmbiguousDetails = async ({
    rowData,
    componentId,
    onFirstPollingResponse,
    createNewValidationOnFailed = false
  }) => {
    const {
      combinationId,
      precondition,
      bomValidationCorrelationId
    } = rowData

    if (!bomValidationCorrelationId) {
      return createAndPollBomValidation({
        combinationId,
        componentId,
        precondition
      })
    }

    const firstPollingResponse = await getVariantTreeBOMValidationDetails({
      combinationId,
      correlationId: bomValidationCorrelationId
    })

        onFirstPollingResponse?.(firstPollingResponse)

        if (firstPollingResponse.status === 'SUCCESS') {
          return mapBomValidationResponseToDetails(
            firstPollingResponse,
            bomValidationCorrelationId
          )
        }

        if (firstPollingResponse.status === 'FAILED') {
          if (!createNewValidationOnFailed || !componentId) {
            throw new Error('BOM validation failed')
          }

          return createAndPollBomValidation({
            combinationId,
            componentId,
            precondition
          })
        }

        const pollingResponse = await pollBomValidationStatus({
          combinationId,
          correlationId: bomValidationCorrelationId
        })

        return mapBomValidationResponseToDetails(
          pollingResponse,
          bomValidationCorrelationId
        )
  }

  const handleCheckDetailsClick = async ({ row, cell, refreshRow }) => {
    const rowData = row.getData()
    const rowState = getAmbiguousRowState(rowData.id)

    if (rowState.isExpanded) {
      setAmbiguousRowState(rowData.id, {
        isExpanded: false
      })

      refreshRow(row)
      return
    }

    if (rowState.isChecking) return

    if (rowState.hasFetchedDetails && rowState.details.length) {
      setAmbiguousRowState(rowData.id, {
        isExpanded: true
      })

      refreshRow(row)
      return
    }

    if (!rowState.isExpanded) {
      skipWaiting.value = false
      isRowDetailsBeingChecked.value = true
    }

    const componentId = getComponentIdFromCell(cell)

    setAmbiguousRowState(rowData.id, {
      isChecking: true,
      isExpanded: false,
      hasFailedDetails: false
    })

    refreshRow(row)

    try {
      const { details, correlationId } = await getAmbiguousDetails({
        rowData,
        componentId,
        createNewValidationOnFailed: true
      })

      await row.update({
        bomValidationCorrelationId: correlationId
      })

      setAmbiguousRowState(rowData.id, {
        isChecking: false,
        isExpanded: true,
        details,
        hasFetchedDetails: true,
        hasFailedDetails: false,
        bomValidationCorrelationId: correlationId
      })
    }
    catch (error) {
      setAmbiguousRowState(rowData.id, {
        isChecking: false,
        isExpanded: false,
        hasFailedDetails: true
      })
    }
    finally {
      refreshRow(row)
      isRowDetailsBeingChecked.value = false
    }
  }

  const handleSkipWaiting = () => {
    skipWaiting.value = true
    isRowDetailsBeingChecked.value = false
  }

  const preloadAmbiguousDetailsForRow = async ({
    row,
    refreshRow,
    onFinished
  }) => {
    const rowData = row.getData()
    const rowState = getAmbiguousRowState(rowData.id)

    if (
      !rowData.isAmbiguous
            || !rowData.bomValidationCorrelationId
            || rowState.hasFetchedDetails
        || rowState.hasFailedDetails
        || rowState.isChecking
    ) {
      return
    }

    try {
      const { details, correlationId } = await getAmbiguousDetails({
        rowData,
        componentId: null,
        onFirstPollingResponse: response => {
          if (response.status === 'IN_PROGRESS') {
            setAmbiguousRowState(rowData.id, {
              isChecking: true,
              isExpanded: false,
              bomValidationCorrelationId: rowData.bomValidationCorrelationId
            })

            refreshRow(row)
          }
        }
      })

      setAmbiguousRowState(rowData.id, {
        isChecking: false,
        isExpanded: true,
        details,
        hasFetchedDetails: true,
        bomValidationCorrelationId: correlationId
      })
    }
    catch (error) {
      setAmbiguousRowState(rowData.id, {
        isChecking: false,
        isExpanded: false,
        hasFailedDetails: true,
        bomValidationCorrelationId: rowData.bomValidationCorrelationId
      })
    }
    finally {
      refreshRow(row)
      onFinished?.()
    }
  }

  const resetAmbiguousRowsState = () => {
    ambiguousRowsState.value = {}
  }

  return {
    ambiguousRowsState,
    skipWaiting,
    getAmbiguousRowState,
    handleSkipWaiting,
    setAmbiguousRowState,
    resetAmbiguousRowsState,
    isRowDetailsBeingChecked,
    getComponentIdFromCell,
    preloadAmbiguousDetailsForRow,
    getHeaderColumnFromCell,
    pollBomValidationStatus,
    getAmbiguousDetails,
    handleCheckDetailsClick
  }
}
